import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { INVOICE_LOGO_BASE64 } from './invoiceLogoBase64';

/* ── Smart Sanitizer (Preserves readable text, handles decorative characters) ── */
const cleanText = (str) => {
  if (str === null || str === undefined) return '';
  const s = String(str).trim();
  // Strip emojis and non-alphanumeric symbols but keep letters, numbers, spaces, and standard punctuation
  const cleaned = s.replace(/[^\w\s.,&'()#/-]/gi, '').trim().toUpperCase();
  return cleaned || s.replace(/[^\x20-\x7E]/g, '').trim().toUpperCase();
};

const formatMoney = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '$0';
  const clean = String(amount).replace(/[^0-9.-]/g, '');
  const num = Number(clean);
  if (!isFinite(num)) return '$0';
  return '$' + num.toLocaleString('en-US', {
    minimumFractionDigits: (num % 1 !== 0 ? 2 : 0),
    maximumFractionDigits: (num % 1 !== 0 ? 2 : 0)
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const str = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{2,4}$/.test(str)) return str;
  const d = new Date(str);
  if (isNaN(d.getTime())) return cleanText(str);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const BRAND = {
  navy: '#0B2545',
  navyDark: '#07162C',
  blue: '#134074',
  blueLight: '#EEF4FB',
  crimson: '#C1121F',
  ink: '#0F172A',
  body: '#334155',
  muted: '#64748B',
  border: '#CBD5E1',
  white: '#FFFFFF',
  tableBg: '#F8FAFC',
};

const S = StyleSheet.create({
  page: {
    paddingTop: 16,
    paddingBottom: 22,
    paddingLeft: 26,
    paddingRight: 26,
    fontFamily: 'Times-Roman',
    fontSize: 8.5,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
  },

  /* ── Header ── */
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 7,
    borderBottomWidth: 1.5,
    borderBottomColor: BRAND.navy,
  },
  logoBox: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 105,
    height: 48,
    objectFit: 'contain',
  },
  companyMeta: {
    marginLeft: 14,
    borderLeftWidth: 1,
    borderLeftColor: BRAND.border,
    paddingLeft: 12,
  },
  companyName: {
    fontFamily: 'Times-Bold',
    fontSize: 11,
    color: BRAND.navy,
    letterSpacing: 0.5,
  },
  companySub: {
    fontSize: 7,
    color: BRAND.muted,
    marginTop: 2,
    letterSpacing: 0.3,
  },
  companyAddr: {
    fontSize: 6.8,
    color: BRAND.body,
    marginTop: 2,
    width: 270,
    lineHeight: 1.25,
  },

  docBadgeBox: {
    alignItems: 'flex-end',
  },
  docTitleBadge: {
    backgroundColor: BRAND.navy,
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 2,
    marginBottom: 4,
  },
  docTitleBadgeReceipt: {
    backgroundColor: '#0F766E',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 2,
    marginBottom: 4,
  },
  docTitleText: {
    fontFamily: 'Times-Bold',
    fontSize: 10.5,
    color: BRAND.white,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  docNumberText: {
    fontFamily: 'Times-Bold',
    fontSize: 10,
    color: BRAND.navy,
    marginTop: 2,
  },
  docDateText: {
    fontSize: 7.5,
    color: BRAND.muted,
    marginTop: 1,
  },

  /* ── Two-Column Information Cards ── */
  cardsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 9,
  },
  infoCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 2,
    overflow: 'hidden',
  },
  cardHeader: {
    backgroundColor: BRAND.navy,
    paddingVertical: 3.5,
    paddingHorizontal: 8,
  },
  cardHeaderReceipt: {
    backgroundColor: '#0F766E',
    paddingVertical: 3.5,
    paddingHorizontal: 8,
  },
  cardHeaderText: {
    fontFamily: 'Times-Bold',
    fontSize: 7.5,
    color: BRAND.white,
    letterSpacing: 0.6,
  },
  cardBody: {
    padding: 6,
    backgroundColor: BRAND.white,
  },
  fieldRow: {
    flexDirection: 'row',
    paddingVertical: 2.2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E2E8F0',
  },
  fieldRowLast: {
    flexDirection: 'row',
    paddingVertical: 2.2,
  },
  fieldLabel: {
    width: 108,
    fontFamily: 'Times-Bold',
    fontSize: 7.2,
    color: BRAND.muted,
    textTransform: 'uppercase',
  },
  fieldValue: {
    flex: 1,
    fontFamily: 'Times-Roman',
    fontSize: 7.5,
    color: BRAND.ink,
    lineHeight: 1.2,
  },
  fieldValueBold: {
    flex: 1,
    fontFamily: 'Times-Bold',
    fontSize: 7.5,
    color: BRAND.navy,
    lineHeight: 1.2,
  },

  /* ── Vehicle Table ── */
  tableSection: {
    marginBottom: 8,
    borderWidth: 1,
    borderColor: BRAND.navy,
    borderRadius: 2,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: BRAND.navy,
    paddingVertical: 4.5,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.navy,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.8,
    borderBottomColor: BRAND.border,
    minHeight: 18,
    alignItems: 'center',
    backgroundColor: BRAND.white,
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottomWidth: 0.8,
    borderBottomColor: BRAND.border,
    minHeight: 18,
    alignItems: 'center',
    backgroundColor: BRAND.tableBg,
  },

  colNo:     { width: '5%',  borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, alignItems: 'center' },
  colMaker:  { width: '15%', borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, paddingLeft: 6 },
  colModel:  { width: '16%', borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, paddingLeft: 6 },
  colYear:   { width: '8%',  borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, alignItems: 'center' },
  colChassis:{ width: '20%', borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, paddingLeft: 6 },
  colCc:     { width: '9%',  borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, alignItems: 'center' },
  colTrans:  { width: '12%', borderRightWidth: 0.8, borderRightColor: BRAND.border, padding: 3, alignItems: 'center' },
  colAmount: { width: '15%', padding: 3, paddingRight: 8, alignItems: 'flex-end' },

  thText: {
    fontFamily: 'Times-Bold',
    fontSize: 7.5,
    color: BRAND.white,
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  tdText: {
    fontFamily: 'Times-Roman',
    fontSize: 7.8,
    color: BRAND.ink,
  },
  tdTextBold: {
    fontFamily: 'Times-Bold',
    fontSize: 8,
    color: BRAND.navy,
  },

  /* ── Balance / Total Banner ── */
  balanceBanner: {
    flexDirection: 'row',
    backgroundColor: BRAND.blueLight,
    borderTopWidth: 1.5,
    borderTopColor: BRAND.navy,
    minHeight: 30,
    alignItems: 'center',
  },
  balanceTitleCol: {
    width: '75%',
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRightWidth: 1.2,
    borderRightColor: BRAND.navy,
    justifyContent: 'center',
  },
  balanceTitleText: {
    fontFamily: 'Times-Bold',
    fontSize: 12,
    color: BRAND.navy,
    letterSpacing: 0.8,
  },
  balanceValueCol: {
    width: '25%',
    paddingVertical: 5,
    paddingHorizontal: 10,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  balanceValueText: {
    fontFamily: 'Times-Bold',
    fontSize: 15,
    color: BRAND.crimson,
    letterSpacing: 0.5,
  },

  /* ── Page 1 Authorized Signature & Seal Block ── */
  endorsementSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 0.8,
    borderTopColor: BRAND.border,
  },
  endorsementBox: {
    width: '45%',
    borderWidth: 0.8,
    borderColor: BRAND.border,
    borderRadius: 2,
    padding: 8,
    backgroundColor: BRAND.tableBg,
  },
  endorsementTitle: {
    fontFamily: 'Times-Bold',
    fontSize: 7.5,
    color: BRAND.navy,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  endorsementSub: {
    fontFamily: 'Times-Roman',
    fontSize: 6.8,
    color: BRAND.muted,
    lineHeight: 1.2,
    marginBottom: 16,
  },
  signLine: {
    borderTopWidth: 1,
    borderTopColor: BRAND.ink,
    paddingTop: 3,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signLineLabel: {
    fontFamily: 'Times-Bold',
    fontSize: 6.8,
    color: BRAND.ink,
  },

  /* ── Terms & Notes (Page 2) ── */
  termsHeading: {
    fontFamily: 'Times-Bold',
    fontSize: 7.5,
    color: BRAND.blue,
    letterSpacing: 0.4,
    marginTop: 4,
    marginBottom: 1.5,
    textTransform: 'uppercase',
  },
  termsText: {
    fontFamily: 'Times-Roman',
    fontSize: 6.5,
    lineHeight: 1.25,
    color: BRAND.body,
  },

  /* ── Corporate Footer ── */
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 26,
    right: 26,
    borderTopWidth: 0.8,
    borderTopColor: BRAND.border,
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontFamily: 'Times-Roman',
    fontSize: 6.5,
    color: BRAND.muted,
  },
  footerTextBold: {
    fontFamily: 'Times-Bold',
    fontSize: 6.5,
    color: BRAND.navy,
  },
});

const InvoicePDF = ({ data = {} }) => {
  const isReceipt = data.docType === 'PAYMENT RECEIPT';
  const label = cleanText(data.paymentLabel || 'BALANCE PAYMENT AMOUNT');
  const totalAmount = formatMoney(data.balance || data.amount);

  // Multi-vehicle handling
  const carList = (data.cars && data.cars.length > 0)
    ? data.cars
    : [{
        maker: data.maker,
        model: data.model,
        year: data.year,
        chassis: data.chassis,
        cc: data.cc,
        transmission: data.transmission,
        amount: data.amount,
      }];

  return (
    <Document title={`${cleanText(data.invoiceNumber) || 'Invoice'} - Platinum Auto Japan`}>
      {/* ═════════════════════════════════════════════════════════════
          PAGE 1 — 100% INVOICE, VEHICLES & AUTHORIZED ENDORSEMENT
          (ZERO TERMS ON THIS PAGE!)
          ═════════════════════════════════════════════════════════════ */}
      <Page size="A4" orientation="landscape" style={S.page}>
        
        {/* Top Executive Header */}
        <View style={S.headerContainer}>
          <View style={S.logoBox}>
            <Image src={INVOICE_LOGO_BASE64} style={S.logo} />
            <View style={S.companyMeta}>
              <Text style={S.companyName}>PLATINUM AUTO JAPAN</Text>
              <Text style={S.companySub}>PREMIUM JAPANESE AUTOMOBILE EXPORTERS</Text>
              <Text style={S.companyAddr}>
                Saitama Prefecture, Yashio City, Midori Area 5-9-5, Dai-2 Kouyou Height-102, Japan 340-0808
              </Text>
            </View>
          </View>
          <View style={S.docBadgeBox}>
            <View style={isReceipt ? S.docTitleBadgeReceipt : S.docTitleBadge}>
              <Text style={S.docTitleText}>
                {isReceipt ? 'OFFICIAL PAYMENT RECEIPT' : 'PRO-FORMA INVOICE'}
              </Text>
            </View>
            <Text style={S.docNumberText}>{cleanText(data.invoiceNumber) || 'INV-PENDING'}</Text>
            <Text style={S.docDateText}>ISSUED: {formatDate(data.issueDate)} · DUE: {formatDate(data.dueDate)}</Text>
          </View>
        </View>

        {/* Two-Column Information Cards */}
        <View style={S.cardsRow}>
          {/* Card 1: Bank Wire & Beneficiary Details */}
          <View style={S.infoCard}>
            <View style={isReceipt ? S.cardHeaderReceipt : S.cardHeader}>
              <Text style={S.cardHeaderText}>OFFICIAL BENEFICIARY BANK DETAILS (REMITTANCE IN USD ONLY)</Text>
            </View>
            <View style={S.cardBody}>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Bank Name:</Text>
                <Text style={S.fieldValueBold}>MIZUHO BANK, LTD</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Bank Address:</Text>
                <Text style={S.fieldValue}>1-15-18, Matsue, Edogawa-Ku, Tokyo, Japan 132-0025</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Beneficiary Account No:</Text>
                <Text style={S.fieldValueBold}>846-7317280</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Beneficiary Account Title:</Text>
                <Text style={S.fieldValueBold}>MIRZA ENTERPRISES.CO.LTD</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>SWIFT Code / BIC:</Text>
                <Text style={S.fieldValueBold}>MHCBJPJT</Text>
              </View>
              <View style={S.fieldRowLast}>
                <Text style={S.fieldLabel}>Beneficiary Address:</Text>
                <Text style={S.fieldValue}>
                  Saitama Prefecture Yashio City Midori Area 5-9-5 Dai-2 Kouyou Height-102, Japan 340-0808
                </Text>
              </View>
            </View>
          </View>

          {/* Card 2: Billed To / Client Particulars */}
          <View style={S.infoCard}>
            <View style={isReceipt ? S.cardHeaderReceipt : S.cardHeader}>
              <Text style={S.cardHeaderText}>CLIENT / CONSIGNEE PARTICULARS</Text>
            </View>
            <View style={S.cardBody}>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Invoice Reference:</Text>
                <Text style={S.fieldValueBold}>{cleanText(data.invoiceNumber) || 'INV-PENDING'}</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Customer Name / ID:</Text>
                <Text style={S.fieldValueBold}>{cleanText(data.customerName) || 'VALUED CLIENT'}</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Customer Address:</Text>
                <Text style={S.fieldValue}>{cleanText(data.customerAddress) || 'NASSAU, BAHAMAS'}</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Customer Contact:</Text>
                <Text style={S.fieldValue}>{cleanText(data.customerContact) || '—'}</Text>
              </View>
              <View style={S.fieldRow}>
                <Text style={S.fieldLabel}>Issue Date:</Text>
                <Text style={S.fieldValue}>{formatDate(data.issueDate)}</Text>
              </View>
              <View style={S.fieldRowLast}>
                <Text style={S.fieldLabel}>Payment Due Date:</Text>
                <Text style={S.fieldValueBold}>{formatDate(data.dueDate)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Multi-Vehicle Specifications Table */}
        <View style={S.tableSection}>
          <View style={S.tableHeaderRow}>
            <View style={S.colNo}><Text style={S.thText}>REC#</Text></View>
            <View style={S.colMaker}><Text style={S.thText}>MAKER</Text></View>
            <View style={S.colModel}><Text style={S.thText}>MODEL</Text></View>
            <View style={S.colYear}><Text style={S.thText}>YEAR</Text></View>
            <View style={S.colChassis}><Text style={S.thText}>CHASSIS ID</Text></View>
            <View style={S.colCc}><Text style={S.thText}>CC</Text></View>
            <View style={S.colTrans}><Text style={S.thText}>TRANSMISSION</Text></View>
            <View style={S.colAmount}><Text style={S.thText}>AMOUNT (USD)</Text></View>
          </View>

          {carList.map((car, idx) => (
            <View key={idx} style={idx % 2 === 1 ? S.tableRowAlt : S.tableRow}>
              <View style={S.colNo}><Text style={S.tdText}>{idx + 1}</Text></View>
              <View style={S.colMaker}><Text style={S.tdTextBold}>{cleanText(car.maker) || '—'}</Text></View>
              <View style={S.colModel}><Text style={S.tdTextBold}>{cleanText(car.model) || '—'}</Text></View>
              <View style={S.colYear}><Text style={S.tdText}>{cleanText(car.year) || '—'}</Text></View>
              <View style={S.colChassis}><Text style={S.tdTextBold}>{cleanText(car.chassis) || '—'}</Text></View>
              <View style={S.colCc}><Text style={S.tdText}>{cleanText(car.cc) || '—'}</Text></View>
              <View style={S.colTrans}><Text style={S.tdText}>{cleanText(car.transmission) || '—'}</Text></View>
              <View style={S.colAmount}><Text style={S.tdTextBold}>{formatMoney(car.amount)}</Text></View>
            </View>
          ))}

          {/* Grand Total Banner */}
          <View style={S.balanceBanner}>
            <View style={S.balanceTitleCol}>
              <Text style={S.balanceTitleText}>{label}</Text>
            </View>
            <View style={S.balanceValueCol}>
              <Text style={S.balanceValueText}>{totalAmount}</Text>
            </View>
          </View>
        </View>

        {/* Authorized Signatory & Client Endorsement Blocks */}
        <View style={S.endorsementSection}>
          <View style={S.endorsementBox}>
            <Text style={S.endorsementTitle}>AUTHORIZED CORPORATE SIGNATORY</Text>
            <Text style={S.endorsementSub}>
              Certified export pro-forma contract issued by Mirza Enterprises Co. Ltd / Platinum Auto Japan, Saitama, Japan.
            </Text>
            <View style={S.signLine}>
              <Text style={S.signLineLabel}>PLATINUM AUTO JAPAN EXPORTS</Text>
              <Text style={S.signLineLabel}>OFFICIAL COMPANY STAMP / SEAL</Text>
            </View>
          </View>

          <View style={S.endorsementBox}>
            <Text style={S.endorsementTitle}>CONSIGNEE ACCEPTANCE &amp; CONFIRMATION</Text>
            <Text style={S.endorsementSub}>
              By signing below or initiating wire remittance, the consignee approves vehicle specifications and terms.
            </Text>
            <View style={S.signLine}>
              <Text style={S.signLineLabel}>AUTHORIZED CLIENT SIGNATURE</Text>
              <Text style={S.signLineLabel}>DATE: ____ / ____ / 2026</Text>
            </View>
          </View>
        </View>

        {/* Page 1 Footer */}
        <View style={S.footer}>
          <Text style={S.footerText}>
            PLATINUM AUTO JAPAN · Saitama Prefecture Yashio City Midori Area 5-9-5 Dai-2 Kouyou Height-102, Japan 340-0808
          </Text>
          <Text style={S.footerTextBold}>PAGE 1 OF 2 · OFFICIAL INVOICE PARTICULARS</Text>
        </View>
      </Page>

      {/* ═════════════════════════════════════════════════════════════
          PAGE 2 — ALL TERMS & CONDITIONS & CORRESPONDENT BANK
          (SECTIONS 1 THROUGH 9 CONSOLIDATED HERE)
          ═════════════════════════════════════════════════════════════ */}
      <Page size="A4" orientation="landscape" style={S.page}>
        
        {/* Header Compact */}
        <View style={[S.headerContainer, { marginBottom: 6 }]}>
          <View style={S.logoBox}>
            <Image src={INVOICE_LOGO_BASE64} style={[S.logo, { width: 85, height: 38 }]} />
            <View style={S.companyMeta}>
              <Text style={S.companyName}>PLATINUM AUTO JAPAN</Text>
              <Text style={S.companySub}>STANDARD TERMS, WARRANTIES &amp; LEGAL JURISDICTION</Text>
            </View>
          </View>
          <View style={S.docBadgeBox}>
            <Text style={S.docNumberText}>{cleanText(data.invoiceNumber) || 'INV-PENDING'}</Text>
            <Text style={S.docDateText}>ANNEXURE: TERMS &amp; CONDITIONS</Text>
          </View>
        </View>

        {/* All Terms Consolidated */}
        <View style={{ gap: 3 }}>
          <Text style={S.termsHeading}>1. INTRODUCTION &amp; BINDING CONTRACT</Text>
          <Text style={S.termsText}>
            These terms and conditions govern your use of the services provided by Platinum Auto Japan, a Japanese used car exporting company. By placing an order, confirming a proforma invoice, or issuing wire remittance, you agree to comply with and be bound by these terms.
          </Text>

          <Text style={S.termsHeading}>2. ORDERS AND PAYMENTS</Text>
          <Text style={S.termsText}>
            <Text style={{ fontFamily: 'Times-Bold' }}>ORDER PROCESS: </Text>
            All orders for vehicles must be placed through our official website or authorized representatives.{"\n"}
            <Text style={{ fontFamily: 'Times-Bold' }}>PAYMENT TERMS: </Text>
            Down payment: customers opting for our financial services are required to make a down payment at the time of placing the order. Balance payment: the remaining balance must be paid upon shipment, as per the current dollar rate in Japan. If the dollar rate drops, the CIF (cost, inspection, and freight) price will increase accordingly.{"\n"}
            <Text style={{ fontFamily: 'Times-Bold' }}>CURRENCY OF PAYMENT: </Text>
            All payments must be made in United States Dollars (USD). Payments in other currencies, including Japanese Yen (JPY), will not be accepted.
          </Text>

          <Text style={S.termsHeading}>3. FINANCIAL SERVICES &amp; REMITTANCE TIMELINES</Text>
          <Text style={S.termsText}>
            <Text style={{ fontFamily: 'Times-Bold' }}>PAYMENT PLANS: </Text>
            Customers using our financial services must adhere to the following conditions: the balance payment must be received within 10 to 15 days after the issuance of the Bill of Lading. Failure to make the balance payment within this period will result in penalties and potential cancellation of the order.{"\n"}
            <Text style={{ fontFamily: 'Times-Bold' }}>PENALTIES AND CANCELLATION: </Text>
            If the balance payment is not received within the specified timeframe, a penalty will be applied. The company reserves the right to cancel the order and sell the vehicle to any other customer without prior notice.
          </Text>

          <Text style={S.termsHeading}>4. SHIPPING AND DELIVERY ESTIMATES</Text>
          <Text style={S.termsText}>
            <Text style={{ fontFamily: 'Times-Bold' }}>SHIPPING: </Text>
            The company will arrange for the shipment of vehicles to the destination specified by the customer at the time of order.{"\n"}
            <Text style={{ fontFamily: 'Times-Bold' }}>DELIVERY TIME: </Text>
            Delivery times are estimated and not guaranteed. Delays caused by customs, shipping companies, or other factors beyond our control are not the responsibility of the company.
          </Text>

          <Text style={S.termsHeading}>5. WARRANTY AND AS-IS LIABILITY DISCLAIMER</Text>
          <Text style={S.termsText}>
            <Text style={{ fontFamily: 'Times-Bold' }}>VEHICLE CONDITION: </Text>
            All vehicles are sold "as is." The company does not provide warranties or guarantees on the condition of used vehicles.{"\n"}
            <Text style={{ fontFamily: 'Times-Bold' }}>LIABILITY: </Text>
            The company is not liable for any damages or losses incurred during shipping. Customers are advised to purchase insurance for their shipments.
          </Text>

          <Text style={S.termsHeading}>6. CUSTOMER OBLIGATIONS &amp; IMPORT COMPLIANCE</Text>
          <Text style={S.termsText}>
            <Text style={{ fontFamily: 'Times-Bold' }}>ACCURATE INFORMATION: </Text>
            Customers must provide accurate and complete information at the time of placing an order. The company is not responsible for issues arising from incorrect information provided by the customer. Compliance with laws: customers must comply with all applicable laws and regulations of their respective countries regarding importation.
          </Text>

          <Text style={S.termsHeading}>7. DISPUTE RESOLUTION &amp; 8. AMENDMENTS &amp; 9. GOVERNING LAW</Text>
          <Text style={S.termsText}>
            Any disputes arising from or related to these terms shall be resolved through amicable negotiation, failing which proceedings shall be subject to arbitration under the Tokyo District Court of Japan. The company reserves the right to amend these terms at any time with notification.
          </Text>
        </View>

        {/* Intermediary Bank Routing Card */}
        <View style={[S.infoCard, { marginTop: 6 }]}>
          <View style={S.cardHeader}>
            <Text style={S.cardHeaderText}>INTERMEDIARY BANK DETAILS (CORRESPONDENT USD ROUTING)</Text>
          </View>
          <View style={[S.cardBody, { flexDirection: 'row', gap: 15 }]}>
            <View style={{ flex: 1 }}>
              <Text style={S.fieldLabel}>Bank Name:</Text>
              <Text style={S.fieldValueBold}>J.P MORGAN CHASE</Text>
            </View>
            <View style={{ flex: 1.5 }}>
              <Text style={S.fieldLabel}>Bank Address:</Text>
              <Text style={S.fieldValue}>270 Park Avenue, New York, N.Y. 10017, USA</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={S.fieldLabel}>SWIFT / BIC Code:</Text>
              <Text style={S.fieldValueBold}>CHASUS33DR</Text>
            </View>
          </View>
        </View>

        {/* Page 2 Footer */}
        <View style={S.footer}>
          <Text style={S.footerText}>
            PLATINUM AUTO JAPAN · Saitama Prefecture Yashio City Midori Area 5-9-5 Dai-2 Kouyou Height-102, Japan 340-0808
          </Text>
          <Text style={S.footerTextBold}>PAGE 2 OF 2 · TERMS, WARRANTIES &amp; LEGAL ANNEXURE</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
